import React, { useRef } from 'react'
import { IGatsbyImageData } from 'gatsby-plugin-image'
import BasicSlideCard from 'src/components/molecules/cards/basicSlideCard'
import DescriptiveCard from 'src/components/molecules/cards/descriptiveCard'
import { generateRandomId } from 'src/utils/domHelper'
import { PrismicLinkType } from 'src/typings/generated/graphql'
import DescriptiveCardLink from 'src/components/molecules/cards/descriptiveCardLink'
import * as Styles from './descriptiveSlider.module.scss'

export type DescriptiveSliderItem = {
  headline?: string
  details?: string
  image?: IGatsbyImageData
  imageAlt?: string
  link?: PrismicLinkType
}

enum DescriptiveSliderCopyLayout {
  standard = 'standard',
  overlay = 'overlay',
  overlayLink = 'overlay with link',
}

export type DescriptiveSliderContent = {
  anchor?: string
  copyLayout?: string
  id?: string
  items?: DescriptiveSliderItem[]
}

type Props = {
  content: DescriptiveSliderContent
}

const DescriptiveSlider = ({ content }: Props) => {
  const {
    anchor = generateRandomId(),
    copyLayout = DescriptiveSliderCopyLayout.standard,
    id = '',
    items = [],
  } = content

  const sliderRef = useRef<HTMLDivElement>(null)

  const copyLayoutFormatted = copyLayout.toLowerCase()

  // Simple scroll navigation
  const scrollLeft = () => {
    if (!sliderRef.current) return
    sliderRef.current.scrollBy({
      left: -300, // Scroll by fixed amount
      behavior: 'smooth',
    })
  }

  const scrollRight = () => {
    if (!sliderRef.current) return
    sliderRef.current.scrollBy({
      left: 300, // Scroll by fixed amount
      behavior: 'smooth',
    })
  }

  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault()
      scrollLeft()
    } else if (event.key === 'ArrowRight') {
      event.preventDefault()
      scrollRight()
    } else if (event.key === 'Home') {
      event.preventDefault()
      if (sliderRef.current) {
        sliderRef.current.scrollTo({ left: 0, behavior: 'smooth' })
      }
    } else if (event.key === 'End') {
      event.preventDefault()
      if (sliderRef.current) {
        sliderRef.current.scrollTo({
          left: sliderRef.current.scrollWidth,
          behavior: 'smooth',
        })
      }
    }
  }

  const slide = (item: DescriptiveSliderItem) => {
    if (copyLayoutFormatted === DescriptiveSliderCopyLayout.overlay) {
      return (
        <div className={Styles.descriptiveCardWrapper}>
          <DescriptiveCard
            title={item.headline ?? ''}
            description={item.details ?? ''}
            image={item.image}
            alt={item.imageAlt ?? ''}
          />
        </div>
      )
    }

    if (copyLayoutFormatted === DescriptiveSliderCopyLayout.overlayLink) {
      return (
        <div className={Styles.descriptiveCardWrapper}>
          <DescriptiveCardLink
            title={item.headline ?? ''}
            image={item.image}
            alt={item.imageAlt ?? ''}
            link={item.link ?? undefined}
          />
        </div>
      )
    }

    return (
      <BasicSlideCard
        headline={item.headline ?? ''}
        details={item.details ?? ''}
        image={item.image}
        alt={item.imageAlt ?? ''}
        link={item.link ?? undefined}
      />
    )
  }

  let sliderClass = Styles.descriptiveSlider

  if (
    copyLayoutFormatted === DescriptiveSliderCopyLayout.overlay ||
    copyLayoutFormatted === DescriptiveSliderCopyLayout.overlayLink
  ) {
    sliderClass += ` ${Styles.overlay}`
  }

  return (
    <section id={anchor} className={sliderClass}>
      {/* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
      <div
        id={`slider-${id}`}
        className={Styles.slider}
        ref={sliderRef}
        tabIndex={0}
        role="group"
        aria-roledescription="carousel"
        aria-label="Raising Canes highlights"
        aria-describedby={`slider-instructions-${id}`}
        onKeyDown={handleKeyDown}
      >
        {items.map((item, i: number) => {
          const key = `descriptive-slide-${i}`
          return (
            <div
              className={Styles.slideWrapper}
              key={key}
              role="group"
              aria-roledescription="slide"
              aria-label={`slide ${i + 1} of ${items.length}`}
            >
              {slide(item)}
            </div>
          )
        })}
      </div>
    </section>
  )
}

export default DescriptiveSlider
